// Security configuration for the application
import { setCSRFToken, getCSRFToken } from '../utils/security';

// Initialize security measures
export const initializeSecurity = () => {
  // Set CSRF token
  setCSRFToken();

  // CSP completely disabled in development
  // Only enable strict CSP in production
  if (process.env.NODE_ENV === 'production') {
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Content-Security-Policy';
    meta.content = "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; img-src 'self' data: https: http: https://i.imgur.com; font-src 'self' https://fonts.gstatic.com; connect-src 'self' https: http: wss:;";
    document.head.appendChild(meta);
  }
  // Development: No CSP - completely disabled for local testing

  // Disable right-click context menu in production
  if (process.env.NODE_ENV === 'production') {
    document.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  console.log('🔓 Security initialized. CSP:', process.env.NODE_ENV === 'production' ? 'enabled' : 'DISABLED (Development Mode)');

  // Prevent drag and drop of external content
  document.addEventListener('dragover', (e) => e.preventDefault());
  document.addEventListener('drop', (e) => e.preventDefault());
};

// API request interceptor for security headers
export const addSecurityHeaders = (config) => {
  const csrfToken = getCSRFToken();

  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }

  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';

  return config;
};

// Input sanitization helpers
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;

  return input
    .replace(/<script[^>]*>.*?<\/script>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
};

export const validateURL = (url) => {
  try {
    const urlObj = new URL(url);

    // Only allow HTTPS in production
    if (process.env.NODE_ENV === 'production' && urlObj.protocol !== 'https:') {
      return false;
    }

    // Block private IP ranges
    const hostname = urlObj.hostname;
    const privateRanges = [
      /^127\./,
      /^10\./,
      /^172\.(1[6-9]|2[0-9]|3[0-1])\./,
      /^192\.168\./,
      /^localhost$/i,
      /^0\.0\.0\.0$/
    ];

    return !privateRanges.some(range => range.test(hostname));
  } catch {
    return false;
  }
};

// File validation
export const validateFile = (file, allowedTypes = [], maxSize = 5 * 1024 * 1024) => {
  if (!file) return { valid: false, error: 'No file provided' };

  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return { valid: false, error: 'File type not allowed' };
  }

  if (file.size > maxSize) {
    return { valid: false, error: 'File size too large' };
  }

  return { valid: true };
};

// Rate limiting
const rateLimitMap = new Map();

export const checkRateLimit = (key, maxRequests = 10, windowMs = 60000) => {
  const now = Date.now();
  const windowStart = now - windowMs;

  if (!rateLimitMap.has(key)) {
    rateLimitMap.set(key, []);
  }

  const requests = rateLimitMap.get(key);
  const validRequests = requests.filter(timestamp => timestamp > windowStart);

  if (validRequests.length >= maxRequests) {
    return false;
  }

  validRequests.push(now);
  rateLimitMap.set(key, validRequests);

  return true;
};